export const PERMISSION_CATEGORIES = {
  panels: "Navigation Panels",
  users: "User Accounts",
  team: "Team Members",
  governance: "Governance Configuration",
  actionItems: "Action Items",
  reports: "Reports & KPI",
  mdReview: "MD Review Workflow",
  hr: "HR Dossier",
  system: "System Administration",
} as const;

export type PermissionCategoryKey = keyof typeof PERMISSION_CATEGORIES;

export interface PermissionDef {
  name: string;
  category: PermissionCategoryKey;
  description: string;
}

export const PERMISSION_CATALOG: ReadonlyArray<PermissionDef> = [
  { name: "view_admin_panel",        category: "panels",      description: "See the Admin nav-link and access the Admin landing page." },
  { name: "view_settings_panel",     category: "panels",      description: "See the Settings nav-link and access System Settings." },

  { name: "manage_users",            category: "users",       description: "Create and edit user accounts (excluding deletion)." },
  { name: "delete_users",            category: "users",       description: "Permanently delete user accounts." },

  { name: "manage_team_members",     category: "team",        description: "Create and edit team-member records." },
  { name: "delete_team_members",     category: "team",        description: "Permanently delete team-member records." },

  { name: "manage_governance",       category: "governance",  description: "Edit governance versions, lanes, actors, reference lists, workflow rules, field permissions, transition permissions." },
  { name: "revise_due_date",         category: "governance",  description: "Override and revise an action item's due date after registration." },

  { name: "create_action_item",      category: "actionItems", description: "Register new action items." },
  { name: "edit_action_item",        category: "actionItems", description: "Edit action items via PATCH (assignee, due date, priority, etc.)." },
  { name: "delete_action_item",      category: "actionItems", description: "Soft-delete an action item." },
  { name: "restore_action_item",     category: "actionItems", description: "Restore a soft-deleted action item." },
  { name: "signoff_action_item",     category: "actionItems", description: "Grant sign-off to an action item." },
  { name: "return_action_item",      category: "actionItems", description: "Return an action item for amendments." },
  { name: "confirm_dropped_the_ball",category: "actionItems", description: "Confirm or dismiss 'dropped-the-ball' escalations raised by other users. Holders of this permission can also flag a stalled reassignment as Insufficient Progress directly, without going through the escalation flow." },
  { name: "postpone_action_item",   category: "actionItems", description: "Owner-only. Postpone an action item's due date with a categorical reason. Hard cap of 2 postpones per task; the 3rd attempt is blocked and the task must be escalated to SDF PMO via Reassign." },

  { name: "view_kpi_dashboard",      category: "reports",     description: "View the KPI dashboard tile." },
  { name: "view_reports",            category: "reports",     description: "Generate and download Excel reports." },

  { name: "view_md_meetings",        category: "mdReview",    description: "Open the MD Meetings review queue (in addition to the SDF MD team-member binding, which is always allowed)." },
  { name: "act_on_md_review",        category: "mdReview",    description: "Approve & close, sign-off & close, or return items from the MD review queue." },
  { name: "mark_dispatched",         category: "mdReview",    description: "Mark approved items as dispatched (in addition to the SDF PMO / Board Secretary bindings, which are always allowed)." },
  { name: "view_awaiting_dispatch",  category: "mdReview",    description: "View the Awaiting Dispatch queue." },

  { name: "view_hr_dossier",         category: "hr",          description: "Open the HR Dossier overdue page." },
  { name: "view_md_review_history",  category: "hr",          description: "View the MD Review History tab inside HR Dossier — per-item reschedule chain, holding time, and SLA verdict for SDF MD review." },
  { name: "view_employee_dossier",   category: "hr",          description: "View the Employee Dossier tab inside HR Dossier — per-person performance report (verdict per item, on-time vs late holding periods, drop count) suitable for an annual review packet." },

  { name: "manage_api_keys",         category: "system",      description: "Create, list, and revoke Agent API keys." },
  { name: "manage_smtp",             category: "system",      description: "Edit SMTP configuration." },
  { name: "manage_branding",         category: "system",      description: "Edit company branding (name, logo, base URL)." },
  { name: "manage_deleted_items",    category: "system",      description: "Open and manage the Deleted Items recycle bin." },
  { name: "view_deleted_items",      category: "system",      description: "Read deleted items list." },
  { name: "manage_backup",           category: "system",      description: "Run database backups and download compiled package." },
  { name: "manage_roles_permissions",category: "system",      description: "Edit the authorization matrix (roles and permissions)." },
  { name: "manage_runtime_config",   category: "system",      description: "Edit the Live Refresh & Performance settings (data freshness, polling intervals, apply method, emergency pause)." },
  { name: "manage_reminder_schedule",category: "system",      description: "Edit the Reminder Schedule (pre-due email offsets) and view the reminder send log." },
  { name: "view_system_logs",       category: "system",      description: "View the System Diagnostics Log (email, auth, database, scheduler, API, and system events)." },
  { name: "manage_chairman_channels",category: "system",      description: "Enrol, re-issue, replace and revoke the Chairman/CEO's WhatsApp number for the Chairman AI channel. Holding this permission means being able to bind a phone number to the Chairman's read-only view of every action item, so it is granted narrowly and every use is audited." },
  { name: "wipe_all_tasks",          category: "system",      description: "Wipe all task data (action items, events, sub-assignments, due-date revisions, MD meeting state, meeting minutes, reminder log) from the database. One-time go-live cutover operation; preserves users, roles, governance config, settings, and team members." },

  { name: "summary_view_own",        category: "actionItems", description: "Summary / action-items list scope — see items where you are the owner, current holder, or have a sub-assignment. Baseline scope; if the role has none of the three summary_view_* permissions the user sees nothing in the list." },
  { name: "summary_view_department", category: "actionItems", description: "Summary / action-items list scope — additionally see items assigned to peers in the same department, but only when the user's team-member record has is_department_head=true. Implies summary_view_own." },
  { name: "summary_view_all",        category: "actionItems", description: "Summary / action-items list scope — see every item across the organisation, bypassing the own/department filter. Implies summary_view_own and summary_view_department." },
] as const;

export const PERMISSION_NAMES = PERMISSION_CATALOG.map((p) => p.name);
export type PermissionName = typeof PERMISSION_CATALOG[number]["name"];

export const PRESET_ROLE_KEYS = [
  "member",
  "md_admin",
  "c_suite",
  "general_admin",
  "hr_admin",
  "it_admin",
  "tech_admin",
  "super_admin",
  "dev_admin",
  // Chairman AI Phase 1. A first-class preset so the Chairman is never an
  // unknown role string, which would fail closed on the matrix but pass every
  // seniority comparison. See shared/v2/chairman.ts.
  "chairman",
] as const;
export type PresetRoleKey = typeof PRESET_ROLE_KEYS[number];

export const PRESET_ROLE_LABELS: Record<PresetRoleKey, string> = {
  member: "Member",
  md_admin: "MD Admin",
  c_suite: "C-Suite",
  general_admin: "General Admin",
  hr_admin: "HR Admin",
  it_admin: "IT Admin",
  tech_admin: "Tech Admin",
  super_admin: "Super Admin",
  dev_admin: "Dev Admin",
  chairman: "Chairman",
};

export const PRESET_ROLE_DESCRIPTIONS: Record<PresetRoleKey, string> = {
  member:        "Standard user. Can view and update items they own. No admin pages.",
  md_admin:      "Proxy for the SDF MD on the MD Meetings workflow. No general admin powers.",
  c_suite:       "Senior reviewer. Confirms or dismisses 'dropped-the-ball' escalations raised by other users. No general admin powers — assign this in addition to the user's normal role.",
  general_admin: "Day-to-day administration: action items, team members, user accounts, KPI, reports.",
  hr_admin:      "Adds user/team-member deletion and HR Dossier access on top of General Admin.",
  it_admin:      "Adds API keys, SMTP, branding, backup, and recycle-bin on top of HR Admin.",
  tech_admin:    "Pure platform/infrastructure role. Owns the Settings page (SMTP, branding, API keys, backups, runtime config, system logs). No Admin-page access, no business view — separation of duties from Super Admin.",
  super_admin:   "Full governance authority: Admin page (governance config, users, roles, KPI, reports, deleted items, reminder schedule). Settings page (SMTP, branding, backups, system logs) lives on Tech Admin instead — separation of duties.",
  dev_admin:     "Hidden system-level keyholder. Cannot be assigned through the UI. Always has every permission.",
  chairman:      "Organisation-wide READ of action items, and nothing else. A READ-ONLY principal: server-side enforcement refuses every state-changing request from this role regardless of the matrix, and meeting minutes are explicitly out of reach. Holds exactly one permission, summary_view_all.",
};

// Only dev_admin remains hard-protected. super_admin is now an editable preset
// (separation of duties: super_admin owns governance/Admin-page; tech_admin owns
// Settings-page) so it MUST be unprotected for the matrix UI to let the admin
// untick its Settings perms after upgrade. dev_admin stays the unkillable
// keyholder fallback for the matrix itself.
export const SYSTEM_PROTECTED_ROLES: ReadonlySet<string> = new Set(["dev_admin"]);

// Hierarchy ladder used ONLY for "who can assign whom" rules. Capability
// checks go through the matrix. Custom roles get a level too — the super
// admin chooses it on creation, bounded by their own level.
export const PRESET_ROLE_LEVELS: Record<PresetRoleKey, number> = {
  member:        0,
  md_admin:      0,
  c_suite:       0,
  general_admin: 1,
  hr_admin:      2,
  it_admin:      3,
  tech_admin:    3,
  super_admin:   4,
  dev_admin:     5,
  // Level 0, deliberately.
  //
  // The ladder governs ONLY "who may assign or deactivate whom" — verified in
  // the post-P0 source: requireRole() and isAdmin are mounted on zero v2
  // routes, and isAnyAdmin() resolves through the matrix, not the ladder. So a
  // level buys the Chairman no capability whatsoever.
  //
  // Given that, the safe level is the LOWEST, not the highest. A high level
  // would only mean "fewer administrators may deactivate this account", which
  // is the opposite of what a governance-sensitive read-only principal wants.
  // Seniority and capability are separate here, and this is the separation.
  chairman:      0,
};

// Custom roles cannot be created at or above this rank from the UI; only
// preset super_admin and the hidden dev_admin sit at this height.
export const MAX_CUSTOM_ROLE_LEVEL = 3;

const GENERAL_ADMIN_PERMS: PermissionName[] = [
  "view_admin_panel",
  "manage_users",
  "manage_team_members",
  "create_action_item",
  "edit_action_item",
  "delete_action_item",
  "signoff_action_item",
  "return_action_item",
  "postpone_action_item",
  "view_kpi_dashboard",
  "view_reports",
  "summary_view_own",
  "summary_view_department",
  "summary_view_all",
];

const HR_ADMIN_ADDITIONAL: PermissionName[] = [
  "delete_users",
  "delete_team_members",
  "view_hr_dossier",
  "view_md_review_history",
  "view_employee_dossier",
];

const IT_ADMIN_ADDITIONAL: PermissionName[] = [
  "view_settings_panel",
  "restore_action_item",
  "manage_api_keys",
  "manage_smtp",
  "manage_branding",
  "manage_deleted_items",
  "view_deleted_items",
  "manage_backup",
  "manage_runtime_config",
  "manage_reminder_schedule",
  "view_system_logs",
];

// Settings-page permissions. As of the separation-of-duties split these live
// on tech_admin (and on legacy it_admin for backward compatibility), but NOT
// on super_admin. The IT Alert Recipients live on the SMTP card, so they're
// implicitly covered by manage_smtp.
const SETTINGS_PAGE_PERMS: PermissionName[] = [
  "view_settings_panel",
  "manage_api_keys",
  "manage_smtp",
  "manage_branding",
  "manage_backup",
  "manage_runtime_config",
  "view_system_logs",
];

// Admin-page extras that historically lived under IT_ADMIN_ADDITIONAL but are
// functionally Admin-page (Deleted Items recycle bin, Reminder Schedule tab,
// restore-from-recycle action). Super admin keeps these; tech admin does not.
const ADMIN_PAGE_OPS_PERMS: PermissionName[] = [
  "restore_action_item",
  "manage_deleted_items",
  "view_deleted_items",
  "manage_reminder_schedule",
];

const SUPER_ADMIN_ADDITIONAL: PermissionName[] = [
  ...ADMIN_PAGE_OPS_PERMS,
  "manage_governance",
  "revise_due_date",
  "manage_roles_permissions",
  "confirm_dropped_the_ball",
  "wipe_all_tasks",
  // Chairman AI Phase 2. Deliberately super-admin-only and deliberately NOT in
  // any inherited bundle: enrolling a number binds it to the Chairman's
  // organisation-wide read, so the set of people who can do it should be the
  // smallest set that can already change who the Chairman is.
  "manage_chairman_channels",
];

const C_SUITE_PERMS: PermissionName[] = [
  "confirm_dropped_the_ball",
  // Default Summary scope for C-Suite: own + department. The data-side
  // is_department_head flag still has to be true for the department widening
  // to actually fire — non-dept-head c_suite users effectively see only own.
  "summary_view_own",
  "summary_view_department",
];

const MD_ADMIN_PERMS: PermissionName[] = [
  "view_md_meetings",
  "act_on_md_review",
  "mark_dispatched",
  "view_awaiting_dispatch",
  // MD Admin proxies the SDF MD on review workflows — they need a full org
  // view of action items to do their job.
  "summary_view_own",
  "summary_view_department",
  "summary_view_all",
  // v2.9.0 — MD Admins (and the SDF MD via this role) need access to the
  // HR Dossier page to reach the new "MD Review History" tab. They do NOT
  // need to see overdue items by holder, but the page itself is gated by
  // view_hr_dossier so we grant it here.
  "view_hr_dossier",
  "view_md_review_history",
];

const HR_ADMIN_PERMS: PermissionName[] = [...GENERAL_ADMIN_PERMS, ...HR_ADMIN_ADDITIONAL];
const IT_ADMIN_PERMS: PermissionName[] = [...HR_ADMIN_PERMS, ...IT_ADMIN_ADDITIONAL];

// Tech Admin: pure Settings-page role. No Admin-page access, no business view.
// Separation of duties from Super Admin — neither role can take the platform
// over alone, dev_admin remains the only role with both keys.
const TECH_ADMIN_PERMS: PermissionName[] = [
  ...SETTINGS_PAGE_PERMS,
];

// Super Admin: governance/Admin-page authority. Explicitly NOT inheriting
// IT_ADMIN_PERMS any more — Settings perms now live on tech_admin only.
const SUPER_ADMIN_PERMS: PermissionName[] = [
  ...HR_ADMIN_PERMS,
  ...MD_ADMIN_PERMS,
  ...SUPER_ADMIN_ADDITIONAL,
];

// Chairman AI Phase 1. Organisation-wide READ of action items, and nothing
// else. Deny-by-default supplies every other refusal: no create_action_item
// (which is also what keeps meeting minutes out of reach), no
// view_admin_panel (so isAnyAdmin and isItemActorAdmin stay false), no
// signoff/edit/delete/return/revise, no manage_*.
//
// The read-only guard in server/v2/readOnlyPrincipal.ts overrides this bundle
// rather than depending on it: even if a permission were added here by
// mistake, a state-changing request from this role is still refused.
const CHAIRMAN_PERMS: PermissionName[] = [
  "summary_view_all",
];

const MEMBER_PERMS: PermissionName[] = [
  // Default Summary scope for plain Members: own only.
  "summary_view_own",
];

export const LEGACY_PRESET_PERMISSIONS: Record<PresetRoleKey, PermissionName[]> = {
  member: [...MEMBER_PERMS],
  md_admin: [...MD_ADMIN_PERMS],
  c_suite: [...C_SUITE_PERMS],
  general_admin: [...GENERAL_ADMIN_PERMS],
  hr_admin: HR_ADMIN_PERMS,
  it_admin: IT_ADMIN_PERMS,
  tech_admin: TECH_ADMIN_PERMS,
  super_admin: SUPER_ADMIN_PERMS,
  dev_admin: [...PERMISSION_NAMES] as PermissionName[],
  chairman: [...CHAIRMAN_PERMS],
};

export function buildPermissionSet(perms: ReadonlyArray<string>): Set<string> {
  return new Set(perms);
}
